import './drilldown-histogram.css'

import { Spin, Tag } from 'antd'
import Plot from 'features/plotly'
import cloneDeep from 'lodash/cloneDeep'
import { PlotMouseEvent } from 'plotly.js'
import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import {
  DEFAULT_HISTOGRAM_COLOR,
  useHistogramConfiguration,
} from './contexts/HistogramConfigurationContext'
import { useHistogramData } from './contexts/HistogramDataContext'
import { MAX_NUMBER_OF_HISTOGRAM_ROWS } from './DrilldownHistogram'
import {
  handlePlotlyClick,
  makeSelectedBarKey,
  PLOTLY_GENERATORS,
} from './plotlyGenerators'

const TruncationWarningContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const BASE_PLOT_CONFIG: Partial<Plotly.Config> = {
  displayModeBar: true,
  displaylogo: false,
  modeBarButtonsToRemove: [
    'zoom2d',
    'pan2d',
    'select2d',
    'lasso2d',
    'resetScale2d',
    'zoomIn2d',
    'zoomOut2d',
    'autoScale2d',
    'resetViewMapbox',
    'hoverClosestCartesian',
    'hoverCompareCartesian',
    'toImage',
  ],
  responsive: true,
}

const BASE_PLOT_LAYOUT: Partial<Plotly.Layout> = {
  title: '',
  xaxis: {
    title: '',
    color: CantileverTheme.colors.gray[400],
  },
  yaxis: {
    title: '',
    color: CantileverTheme.colors.gray[400],
    ticklabelposition: 'inside top',
    tickfont: {
      color: CantileverTheme.colors.gray[400],
    },
  },
  plot_bgcolor: 'rgba(0, 0, 0, 0)',
  paper_bgcolor: 'rgba(0, 0, 0, 0)',
  autosize: true,
}

export const DEFAULT_PLOT_TYPE = 'bar'
const DEFAULT_ORIENTATION = 'h'

export const DrilldownHistogramRenderer: FC<{
  handleBarClick: (category: string, stackIdx: number | null) => void
}> = ({ handleBarClick }) => {
  const { isLoading, histogram, didTruncationOccur, fullRowCount } =
    useHistogramData()
  const {
    plotType: _plotType,
    orientation: _orientation,
    color: _color,
    selectedBarIds: _selectedBarIds,
    stackingGroups: allStackingGroups,
  } = useHistogramConfiguration()
  const stackingGroups = useMemo(
    () => allStackingGroups?.filter((sg) => sg.enabled),
    [allStackingGroups]
  )
  const plotType = _plotType ?? DEFAULT_PLOT_TYPE
  const orientation = _orientation ?? DEFAULT_ORIENTATION
  const color = _color ?? DEFAULT_HISTOGRAM_COLOR
  const selectedBarIds = useMemo(
    () => new Set(_selectedBarIds ?? []),
    [_selectedBarIds]
  )

  const { getPlotlyData, getPlotlyLayout } = PLOTLY_GENERATORS[plotType]
  const data = useMemo(
    () =>
      getPlotlyData(
        histogram,
        orientation,
        stackingGroups ?? [],
        new Set(
          [...selectedBarIds].map(([category, stackIdx]) =>
            makeSelectedBarKey(category, stackIdx)
          )
        ),
        color
      ),
    [
      color,
      getPlotlyData,
      histogram,
      orientation,
      selectedBarIds,
      stackingGroups,
    ]
  )
  const layout = useMemo(
    () =>
      getPlotlyLayout(
        undefined,
        orientation,
        histogram.sortedCategories.length
      ),
    [getPlotlyLayout, histogram.sortedCategories.length, orientation]
  )

  // DON'T MEMO THIS - Plotly modifies it inernally, which breaks stuff
  const histogramLayout: Partial<Plotly.Layout> = {
    ...cloneDeep(BASE_PLOT_LAYOUT),
    ...cloneDeep(layout),
  }

  const handleClick = useCallback(
    (event: PlotMouseEvent) =>
      handlePlotlyClick(
        event,
        handleBarClick,
        data,
        plotType === 'stacked',
        histogram.sortedCategories
      ),
    [data, handleBarClick, histogram.sortedCategories, plotType]
  )
  return (
    <Spin spinning={isLoading} wrapperClassName='histogram-spin-root'>
      <Plot
        style={{ width: '100%' }}
        data={[...data] as Plotly.Data[]}
        layout={histogramLayout}
        config={cloneDeep(BASE_PLOT_CONFIG)}
        onClick={handleClick}
        useResizeHandler={true}
      />
      {didTruncationOccur && (
        <TruncationWarningContainer>
          <Tag>
            Showing {MAX_NUMBER_OF_HISTOGRAM_ROWS} of {fullRowCount} results
          </Tag>
        </TruncationWarningContainer>
      )}
    </Spin>
  )
}
